/**
 * ModerationTab component for reviewing flagged comments.
 *
 * Features:
 * - Table of flagged comments with user/essay context
 * - Approve (unflag) or Delete actions
 * - Block user action
 * - Empty state when no flagged comments
 *
 * Called by:
 * - AdminPage.tsx Moderation tab
 */
import { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Empty,
  Spin,
  Avatar,
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  StopOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFlaggedComments,
  unflagComment,
  deleteFlaggedComment,
  blockUser,
  type FlaggedComment,
} from '../../services/api';

const { Text, Paragraph } = Typography;

function ModerationTab() {
  const queryClient = useQueryClient();
  const [actionId, setActionId] = useState<number | null>(null);

  const { data: flaggedComments, isLoading } = useQuery({
    queryKey: ['flaggedComments'],
    queryFn: getFlaggedComments,
  });

  const handleApprove = async (commentId: number) => {
    setActionId(commentId);
    try {
      await unflagComment(commentId);
      message.success('Comment approved');
      queryClient.invalidateQueries({ queryKey: ['flaggedComments'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to approve comment');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (commentId: number) => {
    setActionId(commentId);
    try {
      await deleteFlaggedComment(commentId);
      message.success('Comment deleted');
      queryClient.invalidateQueries({ queryKey: ['flaggedComments'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to delete comment');
    } finally {
      setActionId(null);
    }
  };

  const handleBlockUser = async (userId: number) => {
    try {
      await blockUser(userId);
      message.success('User blocked');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to block user');
    }
  };

  const columns: ColumnsType<FlaggedComment> = [
    {
      title: 'Comment',
      key: 'content',
      width: 300,
      render: (_, record) => (
        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{ color: '#d9d9d9', marginBottom: 0 }}
        >
          {record.content}
        </Paragraph>
      ),
    },
    {
      title: 'Reason',
      key: 'flagReason',
      width: 150,
      responsive: ['sm', 'md', 'lg', 'xl', 'xxl'] as any,
      render: (_, record) => (
        <Tag color="red" icon={<WarningOutlined />}>
          {record.flagReason || 'unknown'}
        </Tag>
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 180,
      render: (_, record) => record.user ? (
        <Space>
          <Avatar
            size="small"
            src={record.user.avatarUrl}
            icon={<UserOutlined />}
          />
          <div>
            <Text style={{ color: '#d9d9d9', display: 'block' }}>{record.user.name}</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>{record.user.email}</Text>
          </div>
        </Space>
      ) : <Text type="secondary">Unknown</Text>,
    },
    {
      title: 'Essay',
      key: 'essay',
      width: 150,
      responsive: ['md', 'lg', 'xl', 'xxl'] as any,
      render: (_, record) => record.essay ? (
        <Text style={{ color: '#d9d9d9' }}>{record.essay.title}</Text>
      ) : <Text type="secondary">Unknown</Text>,
    },
    {
      title: 'Date',
      key: 'createdAt',
      width: 100,
      responsive: ['sm', 'md', 'lg', 'xl', 'xxl'] as any,
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {new Date(record.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={actionId === record.id}
            onClick={() => handleApprove(record.id)}
          >
            Approve
          </Button>
          <Popconfirm
            title="Delete this comment?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={actionId === record.id}
            >
              Delete
            </Button>
          </Popconfirm>
          {record.user && (
            <Popconfirm
              title="Block this user?"
              description="They will no longer be able to comment."
              onConfirm={() => handleBlockUser(record.user!.id)}
              okText="Block"
              okType="danger"
            >
              <Button size="small" icon={<StopOutlined />}>
                Block
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!flaggedComments || flaggedComments.length === 0) {
    return (
      <Empty
        description={
          <Text type="secondary">No flagged comments. All clear!</Text>
        }
        style={{ padding: '48px' }}
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={flaggedComments}
      rowKey="id"
      scroll={{ x: 600 }}
      pagination={{ pageSize: 20 }}
      size="small"
      style={{ background: '#141414' }}
    />
  );
}

export default ModerationTab;
